import FormikTextInput from '@/shared/forms/components/FormikTextInput'
import FormikDatePicker from '@/shared/forms/components/FormikDatePicker'
import { add } from 'date-fns'
import { OLDEST_ALLOWED_AGE, YOUNGEST_ALLOWED_AGE } from '@/lib/constants'
import GooglePlacesAutocompleteInput from '@/shared/forms/components/GooglePlacesAutocompleteInput'
import * as React from 'react'
import { useCallback } from 'react'
import { GooglePlacesAddress } from '@/lib/apis/types'
import { FormikErrors } from 'formik/dist/types'
import FormikDropdown from '@/shared/forms/components/FormikDropdown'
import { PatientProfileForm } from '@/lib/apis/validators'
import { WebConfig } from '@/lib/config'
import FormikToggle from '@/shared/forms/components/FormikToggle'

export type PersonalInformationFormFieldsProps = {
  values: PatientProfileForm
  setValues: (
    values: PatientProfileForm,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<PatientProfileForm>>
  disabledFields?: Set<string>
  verifiedFields?: Set<string>
}

export const AU_STATES = ['ACT', 'NSW', 'NT', 'SA', 'TAS', 'VIC', 'WA', 'QLD']

export default function PersonalInformationFormFields({
  values,
  setValues,
  disabledFields = new Set(),
  verifiedFields = new Set(),
}: PersonalInformationFormFieldsProps) {
  const onPickAddress = useCallback(
    (address: GooglePlacesAddress) => {
      setValues({ ...values, ...address }).then()
    },
    [setValues, values],
  )

  return (
    <>
      <FormikTextInput
        size="1/2"
        label="First Name"
        autocomplete="given-name"
        name="firstName"
        disabled={disabledFields?.has('firstName')}
      />
      <FormikTextInput
        size="1/2"
        label="Last Name"
        autocomplete="family-name"
        name="lastName"
        disabled={disabledFields?.has('lastName')}
      />
      <FormikDropdown
        size="full"
        label="Gender"
        autocomplete="gender"
        name="gender"
        options={[
          { id: 'M', label: 'Male' },
          { id: 'F', label: 'Female' },
        ]}
        emptyLabel="Select Gender"
        disabled={disabledFields?.has('gender')}
      />

      <FormikTextInput
        size="1/2"
        label="Mobile"
        autocomplete="mobile"
        name="mobile"
        placeholder="0468895000"
        inputMode="numeric"
        disabled={disabledFields?.has('mobile')}
        verified={verifiedFields?.has('mobile')}
      />
      <FormikTextInput
        size="1/2"
        label="Email"
        autocomplete="email"
        name="email"
        disabled={disabledFields?.has('email')}
        verified={verifiedFields?.has('email')}
      />

      <FormikDatePicker
        label="Date of birth"
        size="full"
        autocomplete="bday"
        name="dob"
        variant="month-and-year"
        fromDate={add(new Date(), { years: -OLDEST_ALLOWED_AGE })}
        toDate={add(new Date(), { years: -YOUNGEST_ALLOWED_AGE })}
        disabled={disabledFields?.has('dob')}
      />

      <GooglePlacesAutocompleteInput
        size="full"
        label="Address"
        autocomplete="street-address"
        name="formattedAddress"
        onPickAddress={onPickAddress}
        disabled={disabledFields?.has('formattedAddress')}
      />
      <FormikTextInput
        size="1/3"
        label="Suburb"
        autocomplete="address-level2"
        name="suburb"
        disabled={disabledFields?.has('suburb')}
      />
      <FormikDropdown
        size="1/3"
        label="State"
        autocomplete="address-level1"
        name="state"
        emptyLabel="Pick a state"
        options={AU_STATES.map((state) => ({ id: state, label: state }))}
        disabled={disabledFields?.has('state')}
      />
      <FormikTextInput
        size="1/3"
        label="Postcode"
        autocomplete="postal-code"
        name="postcode"
        inputMode="numeric"
        disabled={disabledFields?.has('postcode')}
      />

      {WebConfig.prescriptionConformanceMode && (
        <>
          <FormikTextInput
            size="full"
            label="Concession Number"
            autocomplete="concession-number"
            name="concessionNumber"
            disabled={disabledFields?.has('concessionNumber')}
          />
          <FormikTextInput
            size="full"
            label="Entitlement Number"
            autocomplete="entitlement-number"
            name="entitlementNumber"
            disabled={disabledFields?.has('entitlementNumber')}
          />
          <FormikToggle
            label="I identify myself as being of Aboriginal and/or Torres Strait Islander origin"
            size="full"
            autocomplete="is-aboriginal-or-torres-strait-islander"
            name="isAbOrTorIslander"
          />
        </>
      )}
    </>
  )
}
